import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, LayoutDashboard, Trophy, User, Shield, Crown } from "lucide-react";
import DeadlineBanner from "@/components/DeadlineBanner";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const tabs = [
    { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/app/competitions", label: "Competitions", icon: Trophy, testId: "nav-competitions" },
    { to: "/app/profile", label: "Profile", icon: User, testId: "nav-profile" },
  ];
  if (user?.role === "competition_admin" || user?.role === "super_admin") {
    tabs.push({ to: "/app/admin", label: "Admin", icon: Shield, testId: "nav-admin" });
  }
  if (user?.role === "super_admin") {
    tabs.push({ to: "/app/super-admin", label: "Super", icon: Crown, testId: "nav-super" });
  }

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="min-h-screen bg-[#071426] text-[#FFFFFF]">
      {/* Top header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#071426]/70 border-b border-[#1F3A5C]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/app/dashboard" className="flex items-center gap-2.5" data-testid="brand-home">
            <div className="w-9 h-9 rounded-xl bg-[#C7F500] flex items-center justify-center relative">
              <Crown className="w-4 h-4 text-[#071426]" strokeWidth={2.4} />
              <div className="absolute -bottom-1 -right-1 text-[9px] font-heading font-black text-[#F5B942] bg-[#071426] rounded px-1 leading-none py-0.5 border border-[#132A45]">TL</div>
            </div>
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-heading text-sm tracking-wider text-white">TOP OF THE LOT</span>
              <span className="text-[9px] uppercase tracking-[0.22em] text-[#AAB7C7] mt-0.5">Football Predictor</span>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {tabs.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                data-testid={t.testId}
                className={({ isActive }) =>
                  `relative px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "text-white" : "text-[#AAB7C7] hover:text-white"
                  } ${isActive ? "after:content-[''] after:absolute after:left-3 after:right-3 after:-bottom-[9px] after:h-0.5 after:bg-[#C7F500] after:rounded-full" : ""}`
                }
              >
                {t.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <DeadlineBanner compact />
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-medium">{user?.name}</span>
              <span className="text-xs text-[#AAB7C7] capitalize">{user?.role?.replace("_", " ")}</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-9 h-9 rounded-lg bg-[#132A45] hover:bg-[#1B4276] flex items-center justify-center transition-colors"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-28 md:pb-10">{children}</main>

      {/* Mobile bottom nav */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#071426] border-t border-[#1F3A5C]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="grid grid-cols-5 max-w-md mx-auto">
          {tabs.slice(0, 5).map((t) => {
            const Icon = t.icon;
            return (
              <NavLink
                key={t.to}
                to={t.to}
                data-testid={`mobile-${t.testId}`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] leading-tight ${
                    isActive ? "text-[#C7F500]" : "text-[#AAB7C7]"
                  }`
                }
              >
                <Icon className="w-5 h-5" />
                {t.label}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
